import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import Select from "react-select";
import {
  Card,
  CardBody,
  Col,
  Container,
  Form,
  Input,
  Label,
  Row,
  Button,
  Table,
  FormGroup,
} from "reactstrap";
import { getCustomers, searchCustomers, exportCustomersToExcel } from "../../components/Api/GmsCustomer";
import CustomerAdvancedSearchModal from "../../components/CustomerAdvancedSearchModal";
import { getBranches } from "../../components/Api/GmsBranch";
import { Eye, SquarePen, Download, Search } from "lucide-react";
import { ClipLoader } from "react-spinners";
import Swal from "sweetalert2";
import {
  getUserPermissions,
  hasPagePermissionWithAdmin,
  hasViewOnlyPermission,
  hasEditOrHigherPermission,
  hasDeletePermissionWithAdmin,
  isUserAdmin
} from "../../components/Permission/PermissionService";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import CustomerProfileModal from "../../components/CustomerProfileModal";

// Custom styles for Select components (from PayrollProcessing)
const selectStyles = {
  control: (provided, state) => ({
    ...provided,
    minHeight: '38px',
    borderColor: state.isFocused ? '#556ee6' : '#ced4da',
    boxShadow: state.isFocused ? '0 0 0 0.2rem rgba(85, 110, 230, 0.25)' : 'none',
  }),
  menu: (provided) => ({
    ...provided,
    zIndex: 9999,
    maxHeight: 200,
  }),
  menuPortal: (base) => ({ ...base, zIndex: 99999 }),
  menuList: (provided) => ({
    ...provided,
    maxHeight: 180,
    overflowY: 'auto',
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected ? '#556ee6' : state.isFocused ? '#f8f9fa' : 'white',
    color: state.isSelected ? 'white' : '#495057',
    '&:hover': {
      backgroundColor: state.isSelected ? '#556ee6' : '#e9ecef',
    },
  }),
};

// Session management
const getUserSession = () => {
  const authUser = JSON.parse(localStorage.getItem("authUser") || "{}");
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  return {
    userId: authUser.userId || 1,
    userName: authUser.userName || "System",
    companyId: authUser.companyId || user.user_Company_Id || null,
    branchId: authUser.branchId || user.user_Branch_Id || null,
  };
};

const emptyFilter = {
  customer_Id: null,
  customer_Code: null,
  customer_NameEn: null,
  customer_NameAr: null,
  customer_IsInternational: null,
  customer_AddressEn: null,
  customer_AddressAr: null,
  customer_Email: null,
  customer_ContactNo: null,
  customer_RepDetails: null,
  customer_Notes: null,
  customer_Active: null,
  customer_Branch_Id: null,
  customer_CreditLimit: null,
  customer_CreatedDate: null,
  customer_CreatedBy: null,
  customer_ModifiedDate: null,
  customer_ModifiedBy: null,
  customer_Type: null,
  company_Name: null,
  customer_ID_Number: null,
  customer_DOB: null,
};

const FormInput = ({ label, id, name, type = "text", value, onChange, error, required = false, placeholder, disabled = false }) => (
  <div className="form-group">
    <Label className="col-form-label" htmlFor={id} style={{ fontSize: '14px', fontWeight: '500', color: '#495057', marginBottom: '6px' }}>
      {label} {required && <span style={{ color: '#dc3545' }}>*</span>}
    </Label>
    <Input
      type={type}
      className={`form-control ${error ? 'is-invalid' : ''}`}
      id={id}
      name={name}
      value={value || ''}
      onChange={onChange}
      placeholder={placeholder}
      required={required}
      disabled={disabled}
    />
    {error && (
      <div className="invalid-feedback d-block" style={{ fontSize: '12px', marginTop: '4px' }}>
        {error}
      </div>
    )}
  </div>
);

const FormSelect = ({ label, id, name, options, value, onChange, error, required = false, disabled = false }) => (
  <div className="form-group">
    <Label className="col-form-label" htmlFor={id} style={{ fontSize: '14px', fontWeight: '500', color: '#495057', marginBottom: '6px' }}>
      {label} {required && <span style={{ color: '#dc3545' }}>*</span>}
    </Label>
    <Select
      id={id}
      name={name}
      options={options}
      filterOption={() => true}
      value={value}
      onChange={onChange}
      isClearable={!required}
      isSearchable
      placeholder={`Select ${label}`}
      classNamePrefix="select2-selection"
      className={error ? 'is-invalid' : ''}
      isDisabled={disabled}
      styles={selectStyles}
      menuPortalTarget={document.body}
    />
    {error && (
      <div className="invalid-feedback d-block" style={{ fontSize: '12px', marginTop: '4px' }}>
        {error}
      </div>
    )}
  </div>
);

export default function CustomerListPage() {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [branches, setBranches] = useState([]);
  const [filter, setFilter] = useState(emptyFilter);
  const [loading, setLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [viewLoadingRow, setViewLoadingRow] = useState(null);
  const [editLoadingRow, setEditLoadingRow] = useState(null);
  const [branchOptions, setBranchOptions] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [profileModalOpen, setProfileModalOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerNameOptions, setCustomerNameOptions] = useState([]);
  const [customerSearchOptions, setCustomerSearchOptions] = useState([]);
  const [customerSearchLoading, setCustomerSearchLoading] = useState(false);
  const [searchTimeout, setSearchTimeout] = useState(null);
  const [showAdvancedSearchModal, setShowAdvancedSearchModal] = useState(false);

  // Permission states
  const [permissions, setPermissions] = useState([]);
  const [permissionsLoading, setPermissionsLoading] = useState(true);
  const [canView, setCanView] = useState(false);
  const [canEdit, setCanEdit] = useState(false);
  const [canDelete, setCanDelete] = useState(false);

  // Get user session
  const session = useMemo(() => getUserSession(), []);

  // Check permissions
  const isViewOnly = hasViewOnlyPermission(permissions, '/GmsCustomerList');
  const hasEditOrHigher = hasEditOrHigherPermission(permissions, '/GmsCustomerList');
  const isAdmin = isUserAdmin();

  useEffect(() => {
    loadPermissions();
  }, []);

  useEffect(() => {
    if (!permissionsLoading && canView) {
      loadBranches();
      // Check for filter params in URL
      const urlParams = new URLSearchParams(window.location.search);
      if (urlParams.toString()) {
        // Restore filters from URL
        const restoredFilter = { ...emptyFilter };
        if (urlParams.get('customer_NameEn')) restoredFilter.customer_NameEn = urlParams.get('customer_NameEn');
        if (urlParams.get('customer_ContactNo')) restoredFilter.customer_ContactNo = urlParams.get('customer_ContactNo');
        const isInternational = urlParams.get('customer_IsInternational');
        if (isInternational !== null) restoredFilter.customer_IsInternational = isInternational === 'true';
        const isActive = urlParams.get('customer_Active');
        if (isActive !== null) restoredFilter.customer_Active = isActive === 'true';
        const branchId = urlParams.get('customer_Branch_Id');
        if (branchId) restoredFilter.customer_Branch_Id = parseInt(branchId);
        const customerType = urlParams.get('customer_Type');
        if (customerType) restoredFilter.customer_Type = parseInt(customerType);
        if (urlParams.get('company_Name')) restoredFilter.company_Name = urlParams.get('company_Name');
        const pageParam = urlParams.get('page');
        if (pageParam) setPage(parseInt(pageParam));

        setFilter(restoredFilter);
        // Show filters if any are applied
        if (Object.values(restoredFilter).some(val => val !== null && val !== '')) {
          setShowFilters(true);
        }
        // Load customers after branches are loaded to ensure branch options are available
        setTimeout(() => {
          loadCustomers(restoredFilter);
        }, 100);
      } else {
        loadCustomers();
      }
    }
  }, [permissionsLoading, canView]);

  const loadPermissions = async () => {
    try {
      const userPermissions = await getUserPermissions();
      setPermissions(userPermissions);

      // Check permissions for this page
      const hasView = hasPagePermissionWithAdmin(userPermissions, '/GmsCustomerList');
      const hasEdit = hasEditOrHigherPermission(userPermissions, '/GmsCustomerList');
      const hasDelete = hasDeletePermissionWithAdmin(userPermissions, '/GmsCustomerList');

      setCanView(hasView);
      setCanEdit(hasEdit);
      setCanDelete(hasDelete);

      // If no view permission, redirect to dashboard
      if (!hasView) {
        Swal.fire("Access Denied", "You don't have permission to view this page.", "error").then(() => {
          window.location.href = '/Dashboard';
        });
      }
    } catch (error) {
      console.error("Error loading permissions:", error);
      Swal.fire("Error", "Failed to load permissions. Redirecting to dashboard.", "error").then(() => {
        window.location.href = '/Dashboard';
      });
    } finally {
      setPermissionsLoading(false);
    }
  };

  const loadBranches = async () => {
    try {
      const branchesData = await getBranches({ branch_Active: true });
      const options = branchesData.map(branch => ({
        value: branch.branch_Id,
        label: branch.branch_NameEn
      }));
      setBranchOptions(options);
      setBranches(branchesData);
    } catch (error) {
      console.error("Error loading branches:", error);
      Swal.fire("Error", "Failed to load branches", "error");
    }
  };

  const loadCustomers = async (filterData = emptyFilter) => {
    try {
      setLoading(true);
      const customersData = await getCustomers(filterData);
      setCustomers(customersData);
      setTotalCount(customersData.length);

      // Generate customer name options for dropdown
      const uniqueCustomerNames = [...new Set(customersData.map(customer => customer.customer_NameEn).filter(Boolean))];
      const nameOptions = uniqueCustomerNames.map(name => ({
        value: name,
        label: name
      }));
      setCustomerNameOptions(nameOptions);
    } catch (error) {
      console.error("Error loading customers:", error);
      Swal.fire("Error", "Failed to load customers", "error");
    } finally {
      setLoading(false);
    }
  };

  // Manual search function for customer name search
  const searchCustomerNamesManually = async (inputValue) => {
    if (!inputValue || inputValue.length < 2) {
      setCustomerSearchOptions([]);
      return;
    }

    try {
      setCustomerSearchLoading(true);
      // Clean input but allow spaces for name search
      const cleanedInput = inputValue.trim();
      const response = await searchCustomers(cleanedInput, 1, 20);

      const formattedOptions = response.items.map(customer => ({
        value: customer.customer_Id, // Use ID as value for uniqueness
        label: `${customer.customer_NameEn || 'N/A'} — ${customer.customer_ContactNo || 'N/A'}`,
        customer: customer
      }));

      setCustomerSearchOptions(formattedOptions);
    } catch (error) {
      console.error('Error searching customers:', error);
      setCustomerSearchOptions([]);
    } finally {
      setCustomerSearchLoading(false);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFilter(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : (value === '' ? null : value)
    }));
  };

  const handleFilterSelectChange = (name, selectedOption) => {
    if (name === 'customer_NameEn') {
      // Special handling for customer name selection to include ID and Contact No
      setFilter(prev => ({
        ...prev,
        customer_NameEn: selectedOption ? selectedOption.customer.customer_NameEn : null,
        customer_Id: selectedOption ? selectedOption.customer.customer_Id : null,
        // Optionally auto-fill contact number if it's empty
        customer_ContactNo: (selectedOption && !prev.customer_ContactNo) ? selectedOption.customer.customer_ContactNo : prev.customer_ContactNo
      }));
    } else {
      setFilter(prev => ({
        ...prev,
        [name]: selectedOption ? selectedOption.value : null
      }));
    }
  };

  const handleApplyFilter = () => {
    setPage(1);
    loadCustomers(filter);
  };

  const handleClearFilter = () => {
    setFilter(emptyFilter);
    setPage(1);
    loadCustomers(emptyFilter);
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  // Get paginated data for display
  const getPaginatedCustomers = () => {
    const startIndex = (page - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return customers.slice(startIndex, endIndex);
  };

  const handleView = (customer) => {
    setSelectedCustomer(customer);
    setProfileModalOpen(true);
  };

  const handleEdit = (customer) => {
    // Encode current filter state in URL
    const filterParams = new URLSearchParams();
    filterParams.append('edit', customer.customer_Id);

    // Add filter values to URL params
    if (filter.customer_NameEn) filterParams.append('customer_NameEn', filter.customer_NameEn);
    if (filter.customer_ContactNo) filterParams.append('customer_ContactNo', filter.customer_ContactNo);
    if (filter.customer_IsInternational !== null) filterParams.append('customer_IsInternational', filter.customer_IsInternational);
    if (filter.customer_Active !== null) filterParams.append('customer_Active', filter.customer_Active);
    if (filter.customer_Branch_Id) filterParams.append('customer_Branch_Id', filter.customer_Branch_Id);
    if (filter.customer_Type !== null) filterParams.append('customer_Type', filter.customer_Type);
    if (filter.company_Name) filterParams.append('company_Name', filter.company_Name);
    if (page > 1) filterParams.append('page', page);

    // Navigate to customer page with edit mode and filters
    navigate(`/GmsCustomer?${filterParams.toString()}`);
  };

  const handleExportExcel = async () => {
    try {
      setLoading(true);
      // Clean filter - remove empty strings and convert to proper format
      const cleanFilter = {
        ...(filter.customer_Id !== null && filter.customer_Id !== undefined && { customer_Id: filter.customer_Id }),
        ...(filter.customer_NameEn && filter.customer_NameEn.trim() !== '' && { customer_NameEn: filter.customer_NameEn }),
        ...(filter.customer_ContactNo && filter.customer_ContactNo.trim() !== '' && { customer_ContactNo: filter.customer_ContactNo }),
        ...(filter.customer_IsInternational !== null && filter.customer_IsInternational !== undefined && { customer_IsInternational: filter.customer_IsInternational }),
        ...(filter.customer_Active !== null && filter.customer_Active !== undefined && { customer_Active: filter.customer_Active }),
        ...(filter.customer_Branch_Id !== null && filter.customer_Branch_Id !== undefined && { customer_Branch_Id: filter.customer_Branch_Id }),
        ...(filter.company_Name && filter.company_Name.trim() !== '' && { company_Name: filter.company_Name })
      };

      await exportCustomersToExcel(cleanFilter);
      Swal.fire("Success", "Customer report exported successfully", "success");
    } catch (error) {
      console.error("Error exporting customers:", error);
      Swal.fire("Error", "Failed to export customer report", "error");
    } finally {
      setLoading(false);
    }
  };

  // Show loading spinner while permissions are loading
  if (permissionsLoading) {
    return (
      <div className="page-content">
        <Container fluid>
          <Row>
            <Col xs={12}>
              <Card>
                <CardBody className="text-center py-5">
                  <ClipLoader size={50} color="#556ee6" />
                  <p className="mt-3">Loading permissions...</p>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    );
  }

  // Don't render if no view permission
  if (!canView) {
    return null;
  }

  return (
    <div className="page-content">
      <Container fluid style={{ paddingTop: '0', paddingBottom: '0' }}>
        <Breadcrumbs title="Customer List" breadcrumbItem="Customer Management" />
        <Row style={{ marginTop: '4px', marginBottom: '0' }}>
          <Col xs={12} style={{ paddingTop: '0' }}>
            <Card style={{ marginTop: '0' }}>
              <CardBody style={{ paddingTop: '4px', paddingBottom: '4px' }}>
                {/* Filter Section */}
                <Card className="mb-1" style={{ marginTop: '0' }}>
                  <CardBody style={{ paddingTop: '12px', paddingBottom: '12px' }}>

                    {showFilters && (
                      <>
                        <Row className="mb-3">
                          <Col md={3}>
                            <div className="mb-3">
                              <Label>Customer Name</Label>
                              <div className="d-flex gap-2">
                                <div style={{ flex: 1 }}>
                                  <Select
                                    id="filter_customer_NameEn"
                                    name="customer_NameEn"
                                    options={customerSearchOptions}
                                    value={filter.customer_Id ? 
                                      customerSearchOptions.find(opt => opt.value === filter.customer_Id) || 
                                      { value: filter.customer_Id, label: filter.customer_NameEn } : 
                                      (filter.customer_NameEn ? { value: null, label: filter.customer_NameEn } : null)
                                    }
                                    onChange={(option) => handleFilterSelectChange('customer_NameEn', option)}
                                    isClearable
                                    isSearchable
                                    placeholder="Type to search customers..."
                                    classNamePrefix="select2-selection"
                                    isLoading={customerSearchLoading}
                                    noOptionsMessage={() => "No customers found"}
                                    loadingMessage={() => "Searching customers..."}
                                    onInputChange={(inputValue) => {
                                      if (searchTimeout) {
                                        clearTimeout(searchTimeout);
                                      }
                                      const timeout = setTimeout(() => {
                                        searchCustomerNamesManually(inputValue);
                                      }, 300);
                                      setSearchTimeout(timeout);
                                      return inputValue;
                                    }}
                                    filterOption={() => true}
                                    styles={selectStyles}
                                    menuPortalTarget={document.body}
                                  />
                                </div>
                                <Button
                                  color="info"
                                  size="sm"
                                  onClick={() => setShowAdvancedSearchModal(true)}
                                  title="Advanced Customer Search"
                                  style={{ height: '38px' }}
                                >
                                  <Search size={16} />
                                </Button>
                              </div>
                            </div>
                          </Col>
                          <Col md={3}>
                            <FormInput
                              label="Contact Number"
                              id="filter_customer_ContactNo"
                              name="customer_ContactNo"
                              value={filter.customer_ContactNo}
                              onChange={handleFilterChange}
                              required={false}
                              placeholder="Filter by contact number"
                            />
                          </Col>
                          <Col md={2}>
                            <FormSelect
                              label="International"
                              id="filter_customer_IsInternational"
                              name="customer_IsInternational"
                              options={[
                                { value: true, label: "Yes" },
                                { value: false, label: "No" }
                              ]}
                              value={filter.customer_IsInternational !== null ?
                                { value: filter.customer_IsInternational, label: filter.customer_IsInternational ? "Yes" : "No" } : null}
                              onChange={(option) => handleFilterSelectChange('customer_IsInternational', option)}
                              required={false}
                            />
                          </Col>
                          <Col md={2}>
                            <FormSelect
                              label="Status"
                              id="filter_customer_Active"
                              name="customer_Active"
                              options={[
                                { value: true, label: "Active" },
                                { value: false, label: "Inactive" }
                              ]}
                              value={filter.customer_Active !== null ?
                                { value: filter.customer_Active, label: filter.customer_Active ? "Active" : "Inactive" } : null}
                              onChange={(option) => handleFilterSelectChange('customer_Active', option)}
                              required={false}
                            />
                          </Col>
                          <Col md={2}>
                            <FormSelect
                              label="Branch"
                              id="filter_customer_Branch_Id"
                              name="customer_Branch_Id"
                              options={branchOptions}
                              value={branchOptions.find(option => option.value === filter.customer_Branch_Id) || null}
                              onChange={(option) => handleFilterSelectChange('customer_Branch_Id', option)}
                              required={false}
                            />
                          </Col>
                        </Row>
                        <Row className="mb-3">
                          <Col md={3}>
                            <FormSelect
                              label="Customer Type"
                              id="filter_customer_Type"
                              name="customer_Type"
                              options={[
                                { value: 1, label: "Individual" },
                                { value: 2, label: "Business" }
                              ]}
                              value={filter.customer_Type !== null ?
                                { value: filter.customer_Type, label: filter.customer_Type === 2 ? "Business" : "Individual" } : null}
                              onChange={(option) => handleFilterSelectChange('customer_Type', option)}
                              required={false}
                            />
                          </Col>
                          <Col md={3}>
                            <FormInput
                              label="Company Name"
                              id="filter_company_Name"
                              name="company_Name"
                              value={filter.company_Name}
                              onChange={handleFilterChange}
                              required={false}
                              placeholder="Filter by company name"
                            />
                          </Col>
                        </Row>
                        <div className="d-flex gap-2">
                          <Button color="primary" onClick={handleApplyFilter}>
                            Apply Filter
                          </Button>
                          <Button color="warning" onClick={handleClearFilter}>
                            Clear
                          </Button>
                        </div>
                      </>
                    )}
                  </CardBody>
                </Card>

                {/* Table Section */}
                <Card style={{ marginTop: '0', marginBottom: '0' }}>
                  <CardBody style={{ paddingTop: '4px', paddingBottom: '4px' }}>
                    <div className="d-flex justify-content-between align-items-center" style={{ marginBottom: '12px', marginTop: '0' }}>
                      <h5 className="card-title mb-0">
                        Customer List ({totalCount} records)
                      </h5>
                      <div className="d-flex gap-2">
                        <Button
                          color="primary"
                          onClick={() => setShowFilters(!showFilters)}
                        >
                          {showFilters ? "Hide Filters" : "Show Filters"}
                        </Button>
                        <Button
                          color="success"
                          size="sm"
                          onClick={handleExportExcel}
                          disabled={loading || totalCount === 0}
                        >
                          <Download className="me-1" size={16} />
                          Export Excel
                        </Button>
                      </div>
                    </div>
                    <div className="table-responsive">
                      <Table bordered>
                        <thead className="table-light">
                          <tr>
                            <th>Sr.No</th>
                            <th>Name</th>
                            <th>Address</th>
                            <th>International</th>
                            <th>Email</th>
                            <th>Contact Number</th>
                            <th>Initial Points</th>
                            <th>Points Balance</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {loading ? (
                            <tr>
                              <td colSpan="9" className="text-center">
                                <ClipLoader size={20} color="#556ee6" />
                              </td>
                            </tr>
                          ) : customers.length === 0 ? (
                            <tr>
                              <td colSpan="9" className="text-center">
                                No customers found
                              </td>
                            </tr>
                          ) : (
                            getPaginatedCustomers().map((customer, index) => (
                              <tr key={customer.customer_Id}>
                                <td>{(page - 1) * pageSize + index + 1}</td>
                                <td>{customer.customer_NameEn}</td>
                                <td>{customer.customer_AddressEn || 'N/A'}</td>
                                <td>
                                  <span className={`badge ${customer.customer_IsInternational ? 'bg-success' : 'bg-secondary'}`}>
                                    {customer.customer_IsInternational ? 'Yes' : 'No'}
                                  </span>
                                </td>
                                <td>{customer.customer_Email || 'N/A'}</td>
                                <td>{customer.customer_ContactNo || 'N/A'}</td>
                                <td>{customer.initialPoints || 0}</td>
                                <td>{customer.pointsBalance || 0}</td>
                                <td>
                                  <div className="d-flex gap-2">
                                    {canEdit && (
                                      <Button
                                        color="info"
                                        size="sm"
                                        onClick={() => handleEdit(customer)}
                                        disabled={editLoadingRow === customer.customer_Id}
                                      >
                                        {editLoadingRow === customer.customer_Id ? (
                                          <ClipLoader size={16} color="#ffffff" />
                                        ) : (
                                          <>
                                            <SquarePen size={16} className="me-1" />
                                            Edit
                                          </>
                                        )}
                                      </Button>
                                    )}
                                    <Button
                                      color="primary"
                                      size="sm"
                                      onClick={() => handleView(customer)}
                                      disabled={viewLoadingRow === customer.customer_Id}
                                    >
                                      {viewLoadingRow === customer.customer_Id ? (
                                        <ClipLoader size={16} color="#ffffff" />
                                      ) : (
                                        <>
                                          <Eye size={16} className="me-1" />
                                          View
                                        </>
                                      )}
                                    </Button>
                                  </div>
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </Table>
                    </div>

                    <div className="d-flex justify-content-between align-items-center mt-4 p-3"
                      style={{ backgroundColor: '#f8f9fa', borderRadius: '0.375rem', border: '1px solid #dee2e6' }}>
                      <div className="text-muted" style={{ fontSize: '14px', fontWeight: '500' }}>
                        <i className="mdi mdi-information-outline me-2" style={{ color: '#556ee6' }}></i>
                        Showing <span style={{ color: '#556ee6', fontWeight: '600' }}>{totalCount > 0 ? (page - 1) * pageSize + 1 : 0}</span> to <span style={{ color: '#556ee6', fontWeight: '600' }}>{Math.min(page * pageSize, totalCount)}</span> of <span style={{ color: '#556ee6', fontWeight: '600' }}>{totalCount}</span> entries
                      </div>
                      {totalCount > pageSize && (() => {
                        const totalPages = Math.ceil(totalCount / pageSize);
                        const getPageNumbers = () => {
                          const pages = [];
                          const maxVisible = 5; // Show 5 page numbers around current

                          if (totalPages <= 7) {
                            // Show all pages if total is 7 or less
                            for (let i = 1; i <= totalPages; i++) {
                              pages.push(i);
                            }
                          } else {
                            // Always show first page
                            pages.push(1);

                            if (page <= 4) {
                              // Near the start: show 1, 2, 3, 4, 5, ..., last
                              for (let i = 2; i <= 5; i++) {
                                pages.push(i);
                              }
                              pages.push('ellipsis');
                              pages.push(totalPages);
                            } else if (page >= totalPages - 3) {
                              // Near the end: show 1, ..., last-4, last-3, last-2, last-1, last
                              pages.push('ellipsis');
                              for (let i = totalPages - 4; i <= totalPages; i++) {
                                pages.push(i);
                              }
                            } else {
                              // In the middle: show 1, ..., current-1, current, current+1, ..., last
                              pages.push('ellipsis');
                              for (let i = page - 1; i <= page + 1; i++) {
                                pages.push(i);
                              }
                              pages.push('ellipsis');
                              pages.push(totalPages);
                            }
                          }

                          return pages;
                        };

                        const pageNumbers = getPageNumbers();

                        return (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            {/* Previous Page Button */}
                            <Button
                              color="light"
                              size="sm"
                              disabled={page === 1}
                              onClick={() => handlePageChange(page - 1)}
                              style={{
                                border: '1px solid #dee2e6',
                                borderRadius: '0.25rem',
                                minWidth: '32px',
                                height: '32px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                backgroundColor: page === 1 ? '#f8f9fa' : '#fff',
                                color: page === 1 ? '#6c757d' : '#495057',
                                padding: 0
                              }}
                            >
                              <i className="mdi mdi-chevron-left" style={{ fontSize: '16px' }}></i>
                            </Button>

                            {/* First Page Button */}
                            <Button
                              color="light"
                              size="sm"
                              disabled={page === 1}
                              onClick={() => handlePageChange(1)}
                              style={{
                                border: '1px solid #dee2e6',
                                borderRadius: '0.25rem',
                                minWidth: '32px',
                                height: '32px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                backgroundColor: page === 1 ? '#f8f9fa' : '#fff',
                                color: page === 1 ? '#6c757d' : '#495057',
                                padding: 0
                              }}
                            >
                              <i className="mdi mdi-chevron-double-left" style={{ fontSize: '16px' }}></i>
                            </Button>

                            {/* Page Numbers */}
                            {pageNumbers.map((pageNum, index) => {
                              if (pageNum === 'ellipsis') {
                                return (
                                  <span
                                    key={`ellipsis-${index}`}
                                    style={{
                                      padding: '0 8px',
                                      color: '#495057',
                                      fontSize: '14px'
                                    }}
                                  >
                                    ...
                                  </span>
                                );
                              }

                              const isActive = page === pageNum;

                              return (
                                <Button
                                  key={pageNum}
                                  color={isActive ? "primary" : "light"}
                                  size="sm"
                                  onClick={() => handlePageChange(pageNum)}
                                  style={{
                                    border: '1px solid #dee2e6',
                                    borderRadius: '0.25rem',
                                    minWidth: '32px',
                                    height: '32px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    backgroundColor: isActive ? '#556ee6' : '#fff',
                                    borderColor: isActive ? '#556ee6' : '#dee2e6',
                                    color: isActive ? '#fff' : '#495057',
                                    fontWeight: isActive ? '600' : '400',
                                    padding: 0,
                                    fontSize: '14px'
                                  }}
                                >
                                  {pageNum}
                                </Button>
                              );
                            })}

                            {/* Next Page Button */}
                            <Button
                              color="light"
                              size="sm"
                              disabled={page === totalPages}
                              onClick={() => handlePageChange(page + 1)}
                              style={{
                                border: '1px solid #dee2e6',
                                borderRadius: '0.25rem',
                                minWidth: '32px',
                                height: '32px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                backgroundColor: page === totalPages ? '#f8f9fa' : '#fff',
                                color: page === totalPages ? '#6c757d' : '#495057',
                                padding: 0
                              }}
                            >
                              <i className="mdi mdi-chevron-right" style={{ fontSize: '16px' }}></i>
                            </Button>

                            {/* Last Page Button */}
                            <Button
                              color="light"
                              size="sm"
                              disabled={page === totalPages}
                              onClick={() => handlePageChange(totalPages)}
                              style={{
                                border: '1px solid #dee2e6',
                                borderRadius: '0.25rem',
                                minWidth: '32px',
                                height: '32px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                backgroundColor: page === totalPages ? '#f8f9fa' : '#fff',
                                color: page === totalPages ? '#6c757d' : '#495057',
                                padding: 0
                              }}
                            >
                              <i className="mdi mdi-chevron-double-right" style={{ fontSize: '16px' }}></i>
                            </Button>
                          </div>
                        );
                      })()}
                    </div>
                  </CardBody>
                </Card>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>

      {/* Customer Profile Modal */}
      <CustomerProfileModal
        isOpen={profileModalOpen}
        toggle={() => setProfileModalOpen(false)}
        customer={selectedCustomer}
        customerId={selectedCustomer?.customer_Id || null}
      />

      {/* Advanced Customer Search Modal */}
      <CustomerAdvancedSearchModal
        isOpen={showAdvancedSearchModal}
        toggle={() => setShowAdvancedSearchModal(false)}
        userBranchId={session.branchId}
        onCustomerSelected={(customer) => {
          // Set the selected customer in the filter using customer_Id for unique identification
          const updatedFilter = { ...emptyFilter, customer_Id: customer.customer_Id };
          setFilter(updatedFilter);
          // Trigger search with updated filter
          setPage(1);
          loadCustomers(updatedFilter);
        }}
      />
    </div>
  );
}

